import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_calendar.css';
import PaginationZeroBased from '@components/common/PaginationZeroBased.jsx';

// 분리된 모달 컴포넌트들 import
import PhotoFrameInputModal from './components/PhotoFrameInputModal.jsx';
import PhotoFrameDetailModal from './components/PhotoFrameDetailModal.jsx';
import PhotoFrameConfirmModal from './components/PhotoFrameConfirmModal.jsx';
import photoFrameApi from '@services/photoFrameApi.jsx';
import { useAuthStore } from '@/store/useAuthStore.jsx';

const PhotoFrameManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // 상태 관리
  const [photoFrames, setPhotoFrames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10,
    hasNext: false,
    hasPrevious: false
  });
  
  // 검색 및 필터 상태
  const [searchFilters, setSearchFilters] = useState({
    frameName: '',
    isDeployed: null,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  
  // 체크박스 상태 관리
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // 모달 메시지 상태
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentFrame, setCurrentFrame] = useState(null);
  
  // 중복확인 상태
  const [isDuplicateChecked, setIsDuplicateChecked] = useState(false);
  const [lastCheckedFrameName, setLastCheckedFrameName] = useState('');
  
  // 모달 입력값 상태 관리
  const [modalInputs, setModalInputs] = useState({
    author: '',
    frameName: '',
    startDatetime: '',
    endDatetime: '',
    isDeployed: false,
    frameDescription: '',
    frameDataFile: null,
    frameDataResourceId: null,
    frameType: 'type1',
    orientation: 'orientation1'
  });
  
  // 지점 관련 상태
  const [availableBranches, setAvailableBranches] = useState([
    { id: 'branch-1', name: '신사점' }
  ]);
  
  const [selectedBranches, setSelectedBranches] = useState([
    { id: 'branch-default', name: '강남점' }
  ]);
  
  const [selectedAvailableBranch, setSelectedAvailableBranch] = useState(null);
  const [selectedAddedBranch, setSelectedAddedBranch] = useState(null);

  // 포토 프레임 목록 조회
  const fetchPhotoFrames = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const params = {
        page,
        size: pagination.size,
        sortBy: searchFilters.sortBy,
        sortDirection: searchFilters.sortDirection,
        ...searchFilters
      };

      // 빈 값 필터링
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await photoFrameApi.getPhotoFrames(params);
      
      if (response.success) {
        // BaseResponse<Page<PhotoFrameResponse>> 구조 처리
        const pageData = response.data;
        setPhotoFrames(pageData.content || []);
        setPagination({
          currentPage: pageData.number || 0,
          totalPages: pageData.totalPages || 0,
          totalElements: pageData.totalElements || 0,
          size: pageData.size || 10,
          hasNext: pageData.hasNext || false,
          hasPrevious: pageData.hasPrevious || false
        });
      }
    } catch (error) {
      console.error('포토 프레임 목록 조회 실패:', error);
      setAlertMessage('포토 프레임 목록을 불러오는데 실패했습니다.');
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  }, [pagination.size, searchFilters]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchPhotoFrames();
  }, []);

  // 필터 변경 시 첫 페이지로 이동하여 재조회
  useEffect(() => {
    if (pagination.currentPage !== 0) {
      fetchPhotoFrames(0);
    } else {
      fetchPhotoFrames();
    }
  }, [searchFilters]);

  // 체크박스 전체 선택/해제 핸들러
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    setSelectedItems(e.target.checked ? photoFrames.map(frame => frame.id) : []);
  };
  
  // 개별 체크박스 핸들러
  const handleItemSelect = (id, e) => {
    if (e.target.checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(item => item !== id));
    }
    
    // 전체 선택 상태 업데이트
    if (e.target.checked && selectedItems.length + 1 === photoFrames.length) {
      setSelectAll(true);
    } else if (!e.target.checked && selectAll) {
      setSelectAll(false);
    }
  };

  // 필터 변경 핸들러
  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // 프레임 추가 버튼 핸들러
  const handleAddFrameClick = () => {
    setModalInputs({
      author: user?.name || 'system',
      frameName: '',
      startDatetime: '',
      endDatetime: '',
      isDeployed: false,
      frameDescription: '',
      frameDataFile: null,
      frameDataResourceId: null,
      frameType: 'type1',
      orientation: 'orientation1'
    });
    
    // 중복확인 상태 초기화
    setIsDuplicateChecked(false);
    setLastCheckedFrameName('');
    
    setAvailableBranches([
      { id: 'branch-1', name: '신사점' }
    ]);
    
    setSelectedBranches([
      { id: 'branch-default', name: '강남점' }
    ]);
    
    setSelectedAvailableBranch(null);
    setSelectedAddedBranch(null);
    
    setShowAddModal(true);
  };
  
  // 편집 버튼 핸들러
  const handleEditClick = (frame) => {
    setCurrentFrame(frame);

    setModalInputs({
      author: user?.name || 'system',
      frameName: frame.frameName,
      startDatetime: formatDateForInput(frame.startDatetime),
      endDatetime: formatDateForInput(frame.endDatetime),
      isDeployed: frame.isDeployed || false,
      frameDescription: frame.frameDescription || '',
      frameDataFile: null,
      frameDataResourceId: frame.frameDataResourceId || null,
      frameType: frame.frameType || 'type1',
      orientation: frame.orientation || 'orientation1'
    });
    
    // 수정모드에서는 기존 이름으로 중복확인 완료 상태로 설정
    setIsDuplicateChecked(true);
    setLastCheckedFrameName(frame.frameName);
    
    // 지점 정보 설정
    const branches = frame.branches || [];
    setSelectedBranches([
      { id: 'branch-default', name: '강남점' },
      ...branches.map(b => ({ id: b.branchId, name: b.branchName }))
    ]);
    
    setAvailableBranches([
      { id: 'branch-1', name: '신사점' }
    ]);
    
    setSelectedAvailableBranch(null);
    setSelectedAddedBranch(null);
    
    setShowEditModal(true);
  };
  
  // 삭제 버튼 핸들러
  const handleDeleteClick = (frame) => {
    setCurrentFrame(frame);
    setConfirmMessage(`${frame.frameName} 프레임을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        const response = await photoFrameApi.deletePhotoFrame(frame.id);
        if (response.success) {
          setAlertMessage(response.message || '삭제되었습니다.');
          setShowAlertModal(true);
          fetchPhotoFrames(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '삭제에 실패했습니다.');
          setShowAlertModal(true);
        }
      } catch (error) {
        console.error('삭제 실패:', error);
        const errorMessage = error.response?.data?.message || '삭제에 실패했습니다.';
        setAlertMessage(errorMessage);
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };
  
  // 중복 확인 핸들러
  const handleCheckDuplicate = async () => {
    const frameName = modalInputs.frameName.trim();
    if (!frameName) {
      setAlertMessage('프레임명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    try {
      const excludeId = showEditModal && currentFrame ? currentFrame.id : null;
      const response = await photoFrameApi.checkFrameNameDuplicate(frameName, excludeId);
      
      if (response.success) {
        const isDuplicate = response.data.isDuplicate;
        if (isDuplicate) {
          setAlertMessage(response.data.message || '이미 사용 중인 프레임명입니다.');
          setIsDuplicateChecked(false);
        } else {
          setAlertMessage(response.data.message || '사용 가능한 프레임명입니다.');
          setIsDuplicateChecked(true);
          setLastCheckedFrameName(frameName);
        }
      } else {
        setAlertMessage(response.message || '중복 확인 실패');
        setIsDuplicateChecked(false);
      }
      setShowAlertModal(true);
    } catch (error) {
      console.error('중복 확인 실패:', error);
      setAlertMessage('중복 확인에 실패했습니다.');
      setIsDuplicateChecked(false);
      setShowAlertModal(true);
    }
  };
  
  // 프레임 추가/수정 확인 핸들러
  const handleConfirm = async () => {
    // 기본 입력값 검증 (중복확인은 모다에서 처리)
    if (!modalInputs.frameName) {
      setAlertMessage('프레임명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!modalInputs.startDatetime || !modalInputs.endDatetime) {
      setAlertMessage('사용 기간을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }

    try {
      // FormData 생성
      const requestData = {
        frameName: modalInputs.frameName,
        startDatetime: modalInputs.startDatetime,
        endDatetime: modalInputs.endDatetime,
        isDeployed: modalInputs.isDeployed,
        frameDescription: modalInputs.frameDescription,
        frameType: modalInputs.frameType,
        orientation: modalInputs.orientation,
        createdBy: modalInputs.author || 'system',
        updatedBy: modalInputs.author || 'system',
        frameDataResourceId: modalInputs.frameDataResourceId,
        // branches: selectedBranches
        //   .filter(b => b.id !== 'branch-default')
        //   .map(b => ({
        //     branchId: b.id,
        //     branchName: b.name,
        //     isActive: true
        //   }))
      };

      if (modalInputs.frameDataFile) {
        requestData.frameDataFile = modalInputs.frameDataFile;
      }

      const formData = requestData;

      if (showAddModal) {
        const response = await photoFrameApi.createPhotoFrame(formData);
        
        if (response.success) {
          setShowAddModal(false);
          setAlertMessage(response.message || '프레임이 생성되었습니다.');
          setShowAlertModal(true);
          fetchPhotoFrames(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '생성에 실패했습니다.');
          setShowAlertModal(true);
        }
      } else if (showEditModal && currentFrame) {
        const response = await photoFrameApi.updatePhotoFrame(currentFrame.id, formData);
        
        if (response.success) {
          setAlertMessage(response.message || '프레임 정보가 수정되었습니다.');
          setShowEditModal(false);
          setShowAlertModal(true);
          
          fetchPhotoFrames(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '수정에 실패했습니다.');
          setShowAlertModal(true);
        }
      }
    } catch (error) {
      console.error('처리 실패:', error);
      setAlertMessage('처리 중 오류가 발생했습니다.');
      setShowAlertModal(true);
    }
  };

  // 일괄 삭제 핸들러
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return;
    
    setConfirmMessage(`선택한 ${selectedItems.length}개의 프레임을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        const response = await photoFrameApi.deletePhotoFrames(selectedItems);
        if (response.success) {
          setSelectedItems([]);
          setSelectAll(false);
          setAlertMessage(response.message || response.data || '선택한 프레임이 삭제되었습니다.');
          setShowAlertModal(true);
          fetchPhotoFrames(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '삭제에 실패했습니다.');
          setShowAlertModal(true);
        }
      } catch (error) {
        console.error('일괄 삭제 실패:', error);
        const errorMessage = error.response?.data?.message || '삭제에 실패했습니다.';
        setAlertMessage(errorMessage);
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    fetchPhotoFrames(page);
  };

  // 리소스 업로드 핸들러 (공통 방식)
  const handleResourceUploaded = (resourceId) => {
    setModalInputs(prev => ({
      ...prev,
      frameDataResourceId: resourceId
    }));
  };

  // 날짜 포맷팅 헬퍼
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '무제한';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // 지점 선택 핸들러
  const handleBranchSelect = (type, id) => {
    if (type === 'available') {
      setSelectedAvailableBranch(id);
      setSelectedAddedBranch(null);
    } else {
      setSelectedAddedBranch(id);
      setSelectedAvailableBranch(null);
    }
  };
  
  // 지점 추가 핸들러
  const handleAddBranch = (branchesToAdd) => {
    if (!branchesToAdd || branchesToAdd.length === 0) return;
    
    if (typeof branchesToAdd === 'string') {
      const branchToAdd = availableBranches.find(b => b.id === branchesToAdd);
      if (!branchToAdd) return;
      
      const isDuplicate = selectedBranches.some(b => b.id === branchToAdd.id);
      if (isDuplicate) return;
      
      setSelectedBranches([...selectedBranches, branchToAdd]);
      setSelectedAvailableBranch(null);
      return;
    }
    
    const newBranches = branchesToAdd.filter(branch => 
      !selectedBranches.some(b => b.id === branch.id)
    );
    
    if (newBranches.length > 0) {
      setSelectedBranches([...selectedBranches, ...newBranches]);
    }
  };
  
  // 지점 제거 핸들러
  const handleRemoveBranch = (branchIds) => {
    if (!branchIds || branchIds.length === 0) return;
    
    if (typeof branchIds === 'string') {
      if (branchIds === 'branch-default') return;
      
      setSelectedBranches(selectedBranches.filter(b => b.id !== branchIds));
      setSelectedAddedBranch(null);
      return;
    }
    
    setSelectedBranches(selectedBranches.filter(b => !branchIds.includes(b.id)));
  };
  
  // 상세정보 아이콘 클릭 핸들러
  const handleInfoClick = (frame) => {
    setCurrentFrame(frame);
    setShowDetailModal(true);
  };

  return (
    <>
      <div className="page-title-container">
        <h1 className="page-title">포토 프레임</h1>
        <div className="page-title-icon">
          <a href="#" id="add-frame-btn" title="포토 프레임 추가" onClick={handleAddFrameClick}>
            <i className="ri-add-circle-line"></i>
          </a>
        </div>
      </div>
      
      <div className="page-container">
        
        {/* 필터 드롭다운 */}
        <div className="filter-dropdown-container-row">
          {/* 일괄 삭제 버튼 */}
          <div className={`bulk-delete-container ${selectedItems.length >= 2 ? 'active' : ''}`}>
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              일괄 삭제 ({selectedItems.length})
            </button>
          </div>

          <div className="filter-dropdown">
            <select 
              value={searchFilters.sortBy} 
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="createdAt">최신순</option>
              <option value="frameName">이름순</option>
              <option value="startDatetime">사용기간순</option>
            </select>
          </div>
        </div>
        
        {/* 프레임 테이블 */}
        <div className="common-table-container">
          {loading ? (
            <div className="loading-container">
              <p>로딩 중...</p>
            </div>
          ) : (
            <table className="common-table">
              <thead>
                <tr>
                  <th>
                    <div className="checkbox-container">
                      <input 
                        type="checkbox" 
                        id="select-all" 
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="select-all" className="custom-checkbox"></label>
                    </div>
                  </th>
                  <th>NO</th>
                  <th>프레임명</th>
                  <th>사용 기간</th>
                  <th>배포 여부</th>
                  <th>상세정보</th>
                  <th>수정</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {photoFrames.map((frame, index) => (
                  <tr key={frame.id}>
                    <td>
                      <div className="checkbox-container">
                        <input 
                          type="checkbox" 
                          id={`frame-${frame.id}`}
                          checked={selectedItems.includes(frame.id)}
                          onChange={(e) => handleItemSelect(frame.id, e)}
                        />
                        <label htmlFor={`frame-${frame.id}`} className="custom-checkbox"></label>
                      </div>
                    </td>
                    <td>{pagination.currentPage * pagination.size + index + 1}</td>
                    <td>{frame.frameName}</td>
                    <td className="usage-period">
                      {formatDateForDisplay(frame.startDatetime)} - {formatDateForDisplay(frame.endDatetime)}
                    </td>
                    <td>{frame.isDeployed ? 'Y' : 'N'}</td>
                    <td>
                      <button className="info-btn" onClick={() => handleInfoClick(frame)}>
                        <i className="ri-information-line"></i>
                      </button>
                    </td>
                    <td>
                      <button className="edit-btn" onClick={() => handleEditClick(frame)}>
                        <i className="ri-pencil-line"></i>
                      </button>
                    </td>
                    <td>
                      <button className="delete-btn" onClick={() => handleDeleteClick(frame)}>
                        <i className="ri-close-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* 페이지네이션 */}
        <PaginationZeroBased
          page={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          size={pagination.size}
          hasNext={pagination.hasNext}
          hasPrevious={pagination.hasPrevious}
          onPageChange={handlePageChange}
        />
      </div>
      
      {/* 프레임 추가 모달 */}
      <PhotoFrameInputModal
        title="포토 프레임 추가"
        isVisible={showAddModal}
        inputs={modalInputs}
        setInputs={setModalInputs}
        onClose={() => setShowAddModal(false)}
        onConfirm={handleConfirm}
        onCheckDuplicate={handleCheckDuplicate}
        availableBranches={availableBranches}
        selectedBranches={selectedBranches}
        onAddBranch={handleAddBranch}
        onRemoveBranch={handleRemoveBranch}
        onBranchSelect={handleBranchSelect}
        selectedAvailableBranch={selectedAvailableBranch}
        selectedAddedBranch={selectedAddedBranch}
        isEdit={false}
        onResourceUploaded={handleResourceUploaded}
        isDuplicateChecked={isDuplicateChecked}
        onFrameNameChange={(name) => {
          if (name !== lastCheckedFrameName) {
            setIsDuplicateChecked(false);
          }
        }}
        onShowAlert={(message) => {
          setAlertMessage(message);
          setShowAlertModal(true);
        }}
      />
      
      {/* 프레임 수정 모달 */}
      <PhotoFrameInputModal
        title="포토 프레임 수정"
        isVisible={showEditModal}
        inputs={modalInputs}
        setInputs={setModalInputs}
        onClose={() => setShowEditModal(false)}
        onConfirm={handleConfirm}
        onCheckDuplicate={handleCheckDuplicate}
        availableBranches={availableBranches}
        selectedBranches={selectedBranches}
        onAddBranch={handleAddBranch}
        onRemoveBranch={handleRemoveBranch}
        onBranchSelect={handleBranchSelect}
        selectedAvailableBranch={selectedAvailableBranch}
        selectedAddedBranch={selectedAddedBranch}
        isEdit={true}
        onResourceUploaded={handleResourceUploaded}
        isDuplicateChecked={isDuplicateChecked}
        onFrameNameChange={(name) => {
          if (name !== lastCheckedFrameName) {
            setIsDuplicateChecked(false);
          }
        }}
        onShowAlert={(message) => {
          setAlertMessage(message);
          setShowAlertModal(true);
        }}
      />
      
      {/* 상세정보 모달 */}
      <PhotoFrameDetailModal
        isVisible={showDetailModal}
        photoFrame={currentFrame}
        onClose={() => setShowDetailModal(false)}
      />
      
      {/* 알림 모달 */}
      <PhotoFrameConfirmModal
        isVisible={showAlertModal}
        title="알림"
        message={alertMessage}
        type="alert"
        onConfirm={() => setShowAlertModal(false)}
        onCancel={() => setShowAlertModal(false)}
      />
      
      {/* 확인 모달 */}
      <PhotoFrameConfirmModal
        isVisible={showConfirmModal}
        title="확인"
        message={confirmMessage}
        type="confirm"
        onConfirm={() => {
          if (confirmCallback) {
            confirmCallback();
          }
          setShowConfirmModal(false);
        }}
        onCancel={() => setShowConfirmModal(false)}
        confirmText="예"
        cancelText="아니오"
      />
    </>
  );
};

export default PhotoFrameManagement;
